import { useQuery } from '@tanstack/react-query'
import type { ComponentProps } from 'react'
import { useNavigate } from 'react-router'
import { HeaderBar } from '@/components/commons/header-bar'
import {
	FileSearch02Icon,
	MarkerPin01Icon,
	User01Icon
} from '@/components/ui/icons'
import { operatorQueryOptions } from '@/features/marquee/query-options'
import {
	isMarketManagerAllowed,
	operatorRoleToLabelKey
} from '@/features/marquee/utils'
import { routes } from '@/routes'
import { useTranslation } from '@/translations/i18n'

/**
 * The app header for Marquee, showing the Marquee operator.
 *
 * The other adapter over [[HeaderBar]]. Marquee reads its own identity and
 * never runs the Luminate probe, so the auth store is empty on these routes and
 * the shared `Header` would render no user menu at all. Name, email and role
 * come from `GET /marquee/auth/me`, already resolved by `MarqueeSessionRoute`
 * and cached for the session, so this costs no request.
 *
 * The role shown here is **the Marquee one**, creator or facilitator, worded
 * from this feature's own map. `luminate_users.role` is a different fact that
 * happens to share the word, and it is the one the sibling adapter shows.
 *
 * There is no reviewer fallback: a backend that cannot say who the operator is
 * does not let them past `MarqueeSessionRoute`, so this never renders without
 * one, and the sign-out on that refusal screen is their way out.
 */
function MarqueeHeader(
	props: Omit<
		ComponentProps<typeof HeaderBar>,
		'user' | 'homeTo' | 'menuActions'
	>
) {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const { data: operator } = useQuery(operatorQueryOptions())

	return (
		<HeaderBar
			{...props}
			homeTo={routes.MARQUEE_EVENTS.handle.fullPath}
			/*
			 * The lookup table is global rather than per event, so it has
			 * nowhere to hang off the tracker or an event's tabs: the account
			 * menu is where a section-wide screen belongs.
			 *
			 * Creator-only, so the entry is hidden rather than disabled for a
			 * facilitator, as every other creator affordance is. The screen is
			 * one table for every market and every event, and an upload
			 * replaces it wholesale, so it belongs to the role that sets events
			 * up; the page states the same thing for anyone arriving by URL.
			 *
			 * Team sits beside it for the same reason: the roster is
			 * section-wide. Admin-only, through `listOperators`. Locations
			 * follows it, admin-only through `createLocation` (OPSSTD-10100).
			 */
			menuActions={[
				...(isMarketManagerAllowed(operator, 'listOperators')
					? [
							{
								label: t('marquee:breadcrumb_team'),
								icon: <User01Icon size={16} />,
								onClickCb: () =>
									navigate(
										routes.MARQUEE_TEAM.handle.fullPath
									)
							}
						]
					: []),
				...(isMarketManagerAllowed(operator, 'createLocation')
					? [
							{
								label: t('marquee:menu_locations'),
								icon: <MarkerPin01Icon size={16} />,
								onClickCb: () =>
									navigate(
										routes.MARQUEE_LOCATIONS.handle.fullPath
									)
							}
						]
					: []),
				...(isMarketManagerAllowed(operator, 'uploadResolutionSource')
					? [
							{
								label: t(
									'marquee:breadcrumb_host_lookup_table'
								),
								icon: <FileSearch02Icon size={16} />,
								onClickCb: () =>
									navigate(
										routes.MARQUEE_HOST_LOOKUP_TABLE.handle
											.fullPath
									)
							}
						]
					: [])
			]}
			user={
				operator && {
					name: operator.name,
					email: operator.email,
					roleLabel: t(operatorRoleToLabelKey[operator.role])
				}
			}
		/>
	)
}

export { MarqueeHeader }
